import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGoBack } from "@/hooks/useGoBack";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Camera,
  ChevronDown,
  ImagePlus,
  Loader2,
  TicketPercent,
} from "lucide-react";
import Modal from "@/components/Modal";
import { CreateBusinessCouponAPI } from "@/services/api/coupon/createBusinessCoupon";
import { GetBusinessPlanAPI } from "@/services/api/business/getBusinessPlan";
import { store } from "@/services/store";
import { cloudinaryUpload } from "@/services/middleware/uploadImage";
import {
  useBusinessCouponTerms,
  type BusinessCouponTermsInputs,
} from "@/hooks/useBusinessCouponTerms";
import {
  COUPON_CATEGORY_I18N_KEYS,
  type CouponCategory,
} from "@/utils/businessCoupon";

type CouponType = "percent" | "amount";

const CATEGORY_CODES = Object.keys(
  COUPON_CATEGORY_I18N_KEYS,
) as CouponCategory[];

const EMPTY_TERMS_INPUTS: Omit<
  BusinessCouponTermsInputs,
  "couponCategory" | "couponType"
> = {
  requiredDiscountValue: "",
  buyQty: "",
  getQty: "",
  freeItemDetail: "",
  bundleDetail: "",
  timeSlotStart: "",
  timeSlotEnd: "",
  timeSlotDetail: "",
  groupMinPeople: "",
  groupDealDetail: "",
  reservationAdvanceDays: "",
  reservationDetail: "",
  freeUpgradeDetail: "",
  addOnDetail: "",
  pointsMultiplierValue: "",
  birthdayDetail: "",
  depositWaiverDetail: "",
  firstTimeDetail: "",
  expiryDate: "",
  hasExpiryPeriod: false,
  expiryPeriod: "",
  extraTerms: "",
};

type FieldName = keyof BusinessCouponTermsInputs;

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  invalid?: boolean;
  className?: string;
}

/** Labelled input used across the form sections. */
const TextField: React.FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  invalid = false,
  className = "",
}) => {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-xs font-semibold text-gray-600">
        {label}
      </span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-xl border bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 ${
          invalid ? "border-red-300" : "border-gray-200 focus:border-[#ffa217]"
        }`}
      />
    </label>
  );
};

/**
 * Create coupon — the web port of the RN `BusinessCouponCreateScreen`. The
 * category-specific "offer details" fields generate the bilingual terms &
 * conditions that are stored on the coupon.
 */
const BusinessCouponCreatePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useGoBack("/portal/coupons");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [couponName, setCouponName] = useState("");
  const [originalPrice, setOriginalPrice] = useState("");
  const [couponImage, setCouponImage] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [category, setCategory] = useState<CouponCategory>("discount");
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [couponType, setCouponType] = useState<CouponType>("percent");
  const [redeemTimesPerUser, setRedeemTimesPerUser] = useState("1");
  const [issuedQuantity, setIssuedQuantity] = useState("");
  const [qrRedemption, setQrRedemption] = useState(false);
  const [terms, setTerms] = useState(EMPTY_TERMS_INPUTS);
  const [showValidation, setShowValidation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alertText, setAlertText] = useState("");
  // A business whose subscription ended (`access_status === "locked"`) cannot
  // create coupons: the server refuses with -40032, and the submit button is
  // disabled up-front so the user learns why before filling the form.
  const [subscriptionLocked, setSubscriptionLocked] = useState(false);

  useEffect(() => {
    // Business sessions only — the route is behind the business portal, but
    // never assume: students/admins skip the fetch entirely.
    if (store.getState().auth?.user_auth !== "business") return;
    let cancelled = false;
    const load = async () => {
      const resp = await GetBusinessPlanAPI();
      if (cancelled) return;
      if (resp?.status === 0 && resp.result?.access_status === "locked") {
        setSubscriptionLocked(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const termsInputs = useMemo<BusinessCouponTermsInputs>(
    () => ({ ...terms, couponCategory: category, couponType }),
    [terms, category, couponType],
  );
  const {
    previewMainText,
    previewFooter,
    isPreviewFallback,
    missingRequiredTerms,
    termsAndConditionsEn,
    termsAndConditionsZhHant,
  } = useBusinessCouponTerms(termsInputs);

  const setTerm = (name: FieldName, value: string) =>
    setTerms((prev) => ({ ...prev, [name]: value }));

  const pickImage = async (file?: File) => {
    if (!file) return;
    setUploadingImage(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = String(reader.result || "");
      setCouponImage(dataUrl);
      const uploaded = await cloudinaryUpload(dataUrl);
      if (uploaded) setCouponImage(uploaded);
      setUploadingImage(false);
    };
    reader.onerror = () => setUploadingImage(false);
    reader.readAsDataURL(file);
  };

  const missingFields = [
    !couponName.trim() && "couponName",
    !Number(originalPrice) && "originalPrice",
    missingRequiredTerms && "offerDetails",
    !Number(redeemTimesPerUser) && "redeemTimesPerUser",
    !Number(issuedQuantity) && "issuedQuantity",
  ].filter(Boolean) as string[];

  const handleSubmit = async () => {
    if (subscriptionLocked) return;
    setShowValidation(true);
    if (missingFields.length > 0 || submitting) return;

    setSubmitting(true);
    const resp = await CreateBusinessCouponAPI({
      coupon_image: couponImage,
      coupon_name: couponName.trim(),
      original_price: String(Number(originalPrice)),
      terms_conditions_en: termsAndConditionsEn,
      terms_conditions_zh_hant: termsAndConditionsZhHant,
      redeem_times_per_user: String(Number(redeemTimesPerUser)),
      issued_quantity: String(Number(issuedQuantity)),
      coupon_category: category,
      offer_type: "coupon",
      scan_by_qr_code: qrRedemption ? 1 : 0,
    });
    setSubmitting(false);

    if (resp?.status === 0) {
      navigate("/portal/coupons");
      return;
    }
    if (resp?.status === -40032) {
      // The server sent the shared ERROR_CODE text; `t` translates it where a
      // resource exists and returns it as-is otherwise.
      setAlertText(
        t(
          resp.result?.message ||
            "Your subscription has ended. Please renew to continue.",
        ),
      );
      return;
    }
    setAlertText(
      t(resp?.status === 2 ? "please_try_again" : "Can`t Create Coupon"),
    );
  };

  const invalid = (name: string) => showValidation && missingFields.includes(name);

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <button
        type="button"
        onClick={goBack}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 transition hover:text-[#ffa217]"
      >
        <ArrowLeft size={16} />
        {t("Back")}
      </button>

      <h1 className="mb-6 text-2xl font-bold text-gray-900">
        {t("Create Coupon")}
      </h1>

      <div className="space-y-6">
        {/* Coupon information */}
        <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-base font-bold text-gray-900">
            {t("Coupon Information")}
          </h2>

          <div className="mb-5 flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-dashed border-[#ffa217] bg-[#fff8f0] transition hover:bg-[#fff3e0]"
            >
              {couponImage ? (
                <img
                  src={couponImage}
                  alt={t("Coupon Icon")}
                  className="h-full w-full object-contain"
                />
              ) : uploadingImage ? (
                <Loader2 size={20} className="animate-spin text-[#ffa217]" />
              ) : (
                <ImagePlus size={22} className="text-[#ffa217]" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => pickImage(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#ffa217] transition hover:text-[#e08f0e]"
            >
              <Camera size={16} />
              {couponImage ? t("Change") : t("Pick an image")}
            </button>
          </div>

          <div className="space-y-4">
            <TextField
              label={t("Coupon Name")}
              value={couponName}
              onChange={setCouponName}
              placeholder={t("Coupon Name")}
              invalid={invalid("couponName")}
            />
            <TextField
              label={t("Original Price (HKD)")}
              value={originalPrice}
              onChange={setOriginalPrice}
              placeholder={t("Original Price (HKD)")}
              type="number"
              invalid={invalid("originalPrice")}
            />

            {/* Category picker */}
            <div className="relative">
              <span className="mb-1 block text-xs font-semibold text-gray-600">
                {t("Coupon Category")}
              </span>
              <button
                type="button"
                onClick={() => setCategoryOpen((open) => !open)}
                className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900"
              >
                <span>{t(COUPON_CATEGORY_I18N_KEYS[category])}</span>
                <ChevronDown size={16} className="text-gray-400" />
              </button>
              {categoryOpen && (
                <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
                  {CATEGORY_CODES.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => {
                        setCategory(code);
                        setCategoryOpen(false);
                      }}
                      className={`block w-full px-3 py-2 text-left text-sm transition hover:bg-[#fff3e0] ${
                        code === category
                          ? "font-semibold text-[#ffa217]"
                          : "text-gray-700"
                      }`}
                    >
                      {t(COUPON_CATEGORY_I18N_KEYS[code])}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Offer details (category specific) + terms preview */}
        <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-base font-bold text-gray-900">
            {t("Offer Details")}
          </h2>

          <div className="space-y-4">
            {(category === "discount" || category === "cashback") && (
              <>
                <div className="flex gap-2">
                  {(
                    [
                      { value: "percent", label: "% Off" },
                      { value: "amount", label: "Amount Off" },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setCouponType(option.value)}
                      className={`rounded-xl px-4 py-2 text-xs font-bold transition ${
                        couponType === option.value
                          ? "bg-[#ffa217] text-white"
                          : "border border-gray-200 text-gray-600"
                      }`}
                    >
                      {t(option.label)}
                    </button>
                  ))}
                </div>
                <TextField
                  label={
                    category === "discount"
                      ? t("Discount amount")
                      : t("Cashback amount")
                  }
                  value={terms.requiredDiscountValue}
                  onChange={(value) => setTerm("requiredDiscountValue", value)}
                  invalid={invalid("offerDetails")}
                />
              </>
            )}

            {category === "bogo" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label={t("Buy Quantity")}
                  value={terms.buyQty}
                  onChange={(value) => setTerm("buyQty", value)}
                  invalid={invalid("offerDetails")}
                />
                <TextField
                  label={t("Free Quantity")}
                  value={terms.getQty}
                  onChange={(value) => setTerm("getQty", value)}
                  invalid={invalid("offerDetails")}
                />
              </div>
            )}

            {category === "freeItem" && (
              <TextField
                label={t("Free Item Details")}
                value={terms.freeItemDetail}
                onChange={(value) => setTerm("freeItemDetail", value)}
                invalid={invalid("offerDetails")}
              />
            )}

            {category === "freeShipping" && (
              <p className="text-sm text-gray-500">
                {t("Free Shipping has no extra details")}
              </p>
            )}

            {category === "bundle" && (
              <TextField
                label={t("Bundle Details")}
                value={terms.bundleDetail}
                onChange={(value) => setTerm("bundleDetail", value)}
                invalid={invalid("offerDetails")}
              />
            )}

            {category === "timeSlotDeal" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label={t("Start time")}
                  value={terms.timeSlotStart}
                  onChange={(value) => setTerm("timeSlotStart", value)}
                />
                <TextField
                  label={t("End time")}
                  value={terms.timeSlotEnd}
                  onChange={(value) => setTerm("timeSlotEnd", value)}
                />
                <TextField
                  className="sm:col-span-2"
                  label={t("Time-slot Deal Details")}
                  value={terms.timeSlotDetail}
                  onChange={(value) => setTerm("timeSlotDetail", value)}
                  invalid={invalid("offerDetails")}
                />
              </div>
            )}

            {category === "groupDeal" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label={t("Minimum people")}
                  value={terms.groupMinPeople}
                  onChange={(value) => setTerm("groupMinPeople", value)}
                />
                <TextField
                  label={t("Group Deal Details")}
                  value={terms.groupDealDetail}
                  onChange={(value) => setTerm("groupDealDetail", value)}
                  invalid={invalid("offerDetails")}
                />
              </div>
            )}

            {category === "reservationDiscount" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label={t("Advance booking days")}
                  value={terms.reservationAdvanceDays}
                  onChange={(value) => setTerm("reservationAdvanceDays", value)}
                />
                <TextField
                  label={t("Reservation Discount Details")}
                  value={terms.reservationDetail}
                  onChange={(value) => setTerm("reservationDetail", value)}
                  invalid={invalid("offerDetails")}
                />
              </div>
            )}

            {category === "freeUpgrade" && (
              <TextField
                label={t("Free Upgrade Details")}
                value={terms.freeUpgradeDetail}
                onChange={(value) => setTerm("freeUpgradeDetail", value)}
                invalid={invalid("offerDetails")}
              />
            )}

            {category === "addOn" && (
              <TextField
                label={t("Add-on Deal Details")}
                value={terms.addOnDetail}
                onChange={(value) => setTerm("addOnDetail", value)}
                invalid={invalid("offerDetails")}
              />
            )}

            {category === "pointsMultiplier" && (
              <TextField
                label={t("Points Multiplier")}
                value={terms.pointsMultiplierValue}
                onChange={(value) => setTerm("pointsMultiplierValue", value)}
                invalid={invalid("offerDetails")}
              />
            )}

            {category === "birthday" && (
              <TextField
                label={t("Birthday Special Details")}
                value={terms.birthdayDetail}
                onChange={(value) => setTerm("birthdayDetail", value)}
                invalid={invalid("offerDetails")}
              />
            )}

            {category === "depositWaiver" && (
              <TextField
                label={t("Deposit Waiver Details")}
                value={terms.depositWaiverDetail}
                onChange={(value) => setTerm("depositWaiverDetail", value)}
                invalid={invalid("offerDetails")}
              />
            )}

            {category === "firstTime" && (
              <TextField
                label={t("First-time Deal Details")}
                value={terms.firstTimeDetail}
                onChange={(value) => setTerm("firstTimeDetail", value)}
                invalid={invalid("offerDetails")}
              />
            )}

            {/* Valid duration */}
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={terms.hasExpiryPeriod}
                onChange={(e) =>
                  setTerms((prev) => ({
                    ...prev,
                    hasExpiryPeriod: e.target.checked,
                    expiryPeriod: e.target.checked ? prev.expiryPeriod : "",
                  }))
                }
                className="h-4 w-4 accent-[#ffa217]"
              />
              {t("Valid Duration")}
            </label>

            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                label={t("Expiry Date")}
                value={terms.expiryDate}
                onChange={(value) => setTerm("expiryDate", value)}
                type="date"
              />
              {terms.hasExpiryPeriod && (
                <TextField
                  label={t("Expiry Period (days)")}
                  value={terms.expiryPeriod}
                  onChange={(value) => setTerm("expiryPeriod", value)}
                  type="number"
                />
              )}
            </div>

            <TextField
              label={t("Extra Terms (Optional)")}
              value={terms.extraTerms}
              onChange={(value) => setTerm("extraTerms", value)}
              placeholder={t("Coupon Terms")}
            />

            {/* Terms preview */}
            <div>
              <p className="mb-1.5 text-xs font-semibold text-gray-600">
                {t("Preview Terms & Conditions")}
              </p>
              <div
                className={`whitespace-pre-line rounded-xl border p-3 text-xs leading-5 ${
                  isPreviewFallback
                    ? "border-dashed border-gray-200 text-gray-400"
                    : "border-gray-100 bg-gray-50 text-gray-600"
                }`}
              >
                {previewMainText}
                {previewFooter ? `\n${previewFooter}` : ""}
              </div>
            </div>
          </div>
        </section>

        {/* Redemption settings */}
        <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-base font-bold text-gray-900">
            {t("Redeem Times & Issued Quantity")}
          </h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label={t("Redeem Times per user")}
              value={redeemTimesPerUser}
              onChange={setRedeemTimesPerUser}
              type="number"
              invalid={invalid("redeemTimesPerUser")}
            />
            <TextField
              label={t("Issued Quantity")}
              value={issuedQuantity}
              onChange={setIssuedQuantity}
              type="number"
              invalid={invalid("issuedQuantity")}
            />
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={qrRedemption}
              onChange={(e) => setQrRedemption(e.target.checked)}
              className="h-4 w-4 accent-[#ffa217]"
            />
            {t("Enable QR Redemption")}
          </label>
        </section>
      </div>

      {subscriptionLocked && (
        <p className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700">
          {t("Your subscription has ended. Please renew to continue.")}
        </p>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || subscriptionLocked}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#ffa217] py-3 text-sm font-bold text-white transition hover:bg-[#f59200] disabled:opacity-60"
      >
        {submitting ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <TicketPercent size={16} />
        )}
        {t("Create Coupon")}
      </button>

      <Modal visible={Boolean(alertText)} onClose={() => setAlertText("")}>
        <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
          <p className="mb-4 text-sm font-semibold text-gray-900">{alertText}</p>
          <button
            type="button"
            onClick={() => setAlertText("")}
            className="w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("Close")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BusinessCouponCreatePage;
